// frontend/src/pages/LoginPage.tsx

import React, { useState, useEffect } from 'react';
import { useAuthStore, rememberMeUtils } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import { loginAPI, getUserByIdAPI, getUserByNameAPI } from '../../services/authService';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PersonIcon from '@mui/icons-material/Person';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CircularProgress from '@mui/material/CircularProgress';
import styles from './LoginPage.module.css';

const LoginPage: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { login, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const handleIdBlur = async () => {
    if (!userId) {
      setUsuario(''); // Limpa o usuário se o ID for apagado
      return;
    }
    setError(''); // Limpa erros anteriores
    try {
      const data = await getUserByIdAPI(userId);
      if (data && data.usuario) {
        setUsuario(data.usuario);
      }
    } catch (err) {
      setError('ID de usuário não encontrado.');
      setUsuario(''); // Limpa o campo de usuário se o ID for inválido
    }
  };

  const handleNameBlur = async () => {
    if (!usuario) {
      setUserId(''); // Limpa o ID se o nome for apagado
      return;
    }
    setError(''); // Limpa erros anteriores
    try {
      const data = await getUserByNameAPI(usuario);
      if (data && data.id) {
        setUserId(String(data.id));
      }
    } catch (err) {
      setError('Usuário não encontrado.');
      setUserId(''); // Limpa o campo de ID se o nome for inválido
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Carrega credenciais salvas se existirem
  useEffect(() => {
    const remembered = rememberMeUtils.get();

    if (remembered.userId) {
      setUserId(remembered.userId);
      setRememberMe(true);

      if (remembered.username) {
        setUsuario(remembered.username);
      }

      if (remembered.password) {
        setPassword(remembered.password);
      }
    }
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // A função loginAPI deve retornar o objeto de dados completo da sua API
      const responseData = await loginAPI(usuario, password);

      if (responseData.token) {
        // Salva ou remove credenciais baseado no checkbox "Lembrar-me"
        if (rememberMe) {
          // Usar o ID retornado pela API se não tiver userId preenchido
          const idParaSalvar = userId || String(responseData.user.id);
          rememberMeUtils.save(idParaSalvar, usuario, password);
        } else {
          rememberMeUtils.clear();
        }

        // Agora passamos o objeto de dados completo para a função login da store.
        login(responseData);

        navigate('/');
      } else {
        setError('Falha no login. Token não recebido.');
      }
    } catch (err) {
      setError('Usuário ou senha inválidos. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginWidget}>
        <div className={styles.loginWidgetForm}>
          <img src="/logo-nome.png" alt="Logo Kadesh" className={styles.logo} />

          {error && <div className={styles.errorMessage}>{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* 1. Container flex para alinhar em linha */}
            <div className={styles.inputRow}>
              <div className={`${styles.inputGroup} ${styles.inputId}`}>
                <label htmlFor="userId">ID</label>
                <div className={styles.inputWithIcon}>
                  <AccountBoxIcon className={styles.inputIcon} sx={{ fontSize: 18 }} />
                  <input
                    type="number"
                    id="userId"
                    placeholder="1234"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    onBlur={handleIdBlur}
                    maxLength={4}
                    onInput={(e) => {
                      const input = e.target as HTMLInputElement;
                      if (input.value.length > 4) {
                        input.value = input.value.slice(0, 4);
                      }
                    }}
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="fname">Usuário</label>
                <div className={styles.inputWithIcon}>
                  <PersonIcon className={styles.inputIcon} sx={{ fontSize: 18 }} />
                  <input
                    type="text"
                    id="fname"
                    placeholder="Digite seu usuário"
                    value={usuario}
                    onChange={(e) => setUsuario(e.target.value)}
                    onBlur={handleNameBlur}
                    required
                  />
                </div>
              </div>
            </div>

            {/* 2. CAMPO DE SENHA */}
            <div className={styles.inputGroup}>
              <label htmlFor="password">Senha</label>
              <div className={styles.inputWithIcon}>
                <LockOutlinedIcon className={styles.inputIcon} sx={{ fontSize: 18 }} />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className={styles.togglePassword}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                </button>
              </div>
            </div>

            {/* Checkbox Lembrar-me */}
            <div className={styles.rememberForgot}>
              <label className={styles.rememberMe}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Lembrar-me</span>
              </label>
            </div>

            <button
              type="submit"
              className={styles.signInButton}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={18} color="inherit" className={styles.spinner} />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          <p className={styles.footer}>© {new Date().getFullYear()} TODOS OS DIREITOS RESERVADOS</p>
        </div>
        <div className={styles.loginWidgetImage}></div>
      </div>
    </div>
  );
};

export default LoginPage;